import { useEffect } from 'react';
import { matchRoutes, useLocation } from 'react-router';
import type { Crumb } from '../components';
import {
  ROUTES,
  ancestry,
  formatTitle,
  href,
  type RouteDef,
  type RouteId,
} from '../routes/manifest';

const MATCHABLE = ROUTES.map((r) => ({ path: r.path, id: r.id }));

export interface CurrentRoute {
  route: RouteDef;
  params: Record<string, string | undefined>;
  title: string;
}

/** The manifest route for a pathname (react-router's ranking: literal segments beat params). */
export function matchManifest(pathname: string): CurrentRoute | null {
  const m = matchRoutes(MATCHABLE, pathname)?.at(-1);
  if (!m) return null;
  const route = ROUTES.find((r) => r.id === m.route.id) as RouteDef;
  const params = m.params as Record<string, string | undefined>;
  return { route, params, title: formatTitle(route, params) };
}

export function useCurrentRoute(): CurrentRoute | null {
  return matchManifest(useLocation().pathname);
}

/** Breadcrumbs from the manifest parent chain; parents that need a missing param get no link. */
export function breadcrumbsFor(current: CurrentRoute): Crumb[] {
  const chain = ancestry(current.route.id);
  return chain.map((r, i) => {
    const label = r.id === 'home' ? 'Home' : formatTitle(r, current.params);
    if (i === chain.length - 1) return { label };
    try {
      return { label, to: href(r.id as RouteId, current.params as Record<string, string>) };
    } catch {
      return { label };
    }
  });
}

/**
 * Per-page document title (WCAG 2.4.2) and focus on the page heading after client-side
 * navigation, so screen readers announce the new page (the first render keeps browser focus).
 */
export function usePageAnnouncements(title: string | undefined) {
  const { pathname } = useLocation();
  useEffect(() => {
    if (title) document.title = `${title} - MantisBT`;
  }, [title]);
  useEffect(() => {
    const w = window as { __mantisNavigated?: boolean };
    if (!w.__mantisNavigated) {
      w.__mantisNavigated = true;
      return;
    }
    document.getElementById('page-title')?.focus();
  }, [pathname]);
}
